import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { AxiosError } from 'axios';
import { VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { OralExamIdRejectionReason } from '@/common/api/models/oralExamIdRejectionReason';
import { useDialog } from '@/common/context/DialogContext';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import { useOralExamIdReview } from '@/common/hooks/oralExam/useOralExamIdReview';
import { useOralExamLabOverrides } from '@/common/oralExam/oralExamLabOverrides';

import { preflightRosterRank } from '../StudentLiveTile';
import { idPhotoUriWithCacheBust, useIdPhotoUrl } from '../useIdPhotoUrl';

import { resolveBandState } from './bandState';
import { IdReviewBand } from './IdReviewBand';
import { RetakeDialog } from './RetakeDialog';
import type { IdConsentReviewItem, IdConsentReviewProps } from './types';

export type { IdConsentReviewItem, IdConsentReviewProps } from './types';

function rosterRank(item: IdConsentReviewItem): number {
  return preflightRosterRank(item.readiness);
}

function labRejectError(): AxiosError {
  return new AxiosError('Network Error', AxiosError.ERR_NETWORK);
}

type MountedRetakeDialogProps = {
  slotId: string;
  item: IdConsentReviewItem;
  selectedCode?: OralExamIdRejectionReason;
  errorMessage?: string;
  pending: boolean;
  onClose: () => void;
  onSelectReason: (code: OralExamIdRejectionReason) => void;
  onSubmit: (note: string) => void;
};

/** Resolves photo URIs inside provider tree; RetakeDialog body stays context-free. */
function MountedRetakeDialog({
  slotId,
  item,
  selectedCode,
  errorMessage,
  pending,
  onClose,
  onSelectReason,
  onSubmit,
}: MountedRetakeDialogProps) {
  const { url: selfieUrl, revision: selfieRevision } = useIdPhotoUrl(
    slotId,
    item.bookingId,
    item.idSelfiePhotoBlobPath,
    'selfie',
  );
  const { url: documentUrl, revision: documentRevision } = useIdPhotoUrl(
    slotId,
    item.bookingId,
    item.idPhotoBlobPath,
    'document',
  );
  const selfieUri = selfieUrl ? idPhotoUriWithCacheBust(selfieUrl, selfieRevision) : null;
  const documentUri = documentUrl ? idPhotoUriWithCacheBust(documentUrl, documentRevision) : null;

  return (
    <RetakeDialog
      isOpen
      bookingId={item.bookingId}
      studentName={item.studentName}
      selfieUri={selfieUri}
      documentUri={documentUri}
      selectedCode={selectedCode}
      errorMessage={errorMessage}
      pending={pending}
      onClose={onClose}
      onSelectReason={onSelectReason}
      onSubmit={onSubmit}
    />
  );
}

/**
 * Per-student ID and consent review with confirm identity / request retake.
 */
export function IdConsentReview({
  slotId, reviewItems, slotStartIso, slotEndIso,
}: IdConsentReviewProps) {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const labOverrides = useOralExamLabOverrides();
  const { queryKey } = useOralExamCohortState(slotId, { polling: false });
  const {
    approve, reject, isApproving, isRejecting,
  } = useOralExamIdReview({ stateQueryKey: queryKey });
  const [rejectingBookingId, setRejectingBookingId] = useState<string | null>(null);
  const [selectedReasonCodes, setSelectedReasonCodes] = useState<Record<string, OralExamIdRejectionReason>>({});
  const [rejectErrors, setRejectErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (labOverrides?.preflightOpenRetakeBookingId) {
      setRejectingBookingId(labOverrides.preflightOpenRetakeBookingId);
    }
  }, [labOverrides?.preflightOpenRetakeBookingId]);

  const handleApprove = useCallback(async (item: IdConsentReviewItem) => {
    await approve({
      courseId: item.courseId,
      moduleId: item.moduleId,
      taskId: item.taskId,
      bookingId: item.bookingId,
      etag: item.etag,
    });
  }, [approve]);

  const clearRejectDraft = useCallback((bookingId: string) => {
    setRejectingBookingId(null);
    setSelectedReasonCodes((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    setRejectErrors((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
  }, []);

  const handleRejectOpen = useCallback((bookingId: string) => {
    setRejectErrors((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    setRejectingBookingId(bookingId);
  }, []);

  const handleRejectSubmit = useCallback(async (item: IdConsentReviewItem, note: string) => {
    const reasonCode = selectedReasonCodes[item.bookingId];
    if (!reasonCode) return;

    const fallbackMessage = t('oralExam.actionFailed', { defaultValue: 'Action failed' });

    try {
      if (labOverrides?.preflightSimulateRejectError) {
        const error = labRejectError();
        const message = getOralExamApiErrorMessage(error, fallbackMessage, t);
        showToastMessage('error', message);
        setRejectErrors((prev) => ({ ...prev, [item.bookingId]: message }));
        return;
      }

      await reject({
        courseId: item.courseId,
        moduleId: item.moduleId,
        taskId: item.taskId,
        bookingId: item.bookingId,
        reasonCode,
        note: note || undefined,
        etag: item.etag,
      });
      clearRejectDraft(item.bookingId);
    } catch (error) {
      const message = getOralExamApiErrorMessage(error, fallbackMessage, t);
      setRejectErrors((prev) => ({ ...prev, [item.bookingId]: message }));
    }
  }, [
    reject,
    selectedReasonCodes,
    clearRejectDraft,
    labOverrides?.preflightSimulateRejectError,
    showToastMessage,
    t,
  ]);

  const pending = isApproving || isRejecting;

  const orderedItems = useMemo(
    () => [...reviewItems].sort((a, b) => rosterRank(a) - rosterRank(b)),
    [reviewItems],
  );
  const rejectingItem = rejectingBookingId
    ? orderedItems.find((item) => item.bookingId === rejectingBookingId)
    : undefined;

  return (
    <>
      <VStack testID="id-consent-review">
        {orderedItems.map((item, index) => (
          <IdReviewBand
            key={item.bookingId}
            slotId={slotId}
            item={item}
            bandState={resolveBandState(item, t)}
            slotStartIso={slotStartIso}
            slotEndIso={slotEndIso}
            pending={pending}
            onApprove={() => handleApprove(item)}
            onRejectOpen={() => handleRejectOpen(item.bookingId)}
            showDivider={index > 0}
            isFirst={index === 0}
          />
        ))}
      </VStack>

      {rejectingItem ? (
        <MountedRetakeDialog
          key={rejectingItem.bookingId}
          slotId={slotId}
          item={rejectingItem}
          selectedCode={selectedReasonCodes[rejectingItem.bookingId]}
          errorMessage={rejectErrors[rejectingItem.bookingId]}
          pending={pending}
          onClose={() => clearRejectDraft(rejectingItem.bookingId)}
          onSelectReason={(code) => setSelectedReasonCodes((prev) => ({
            ...prev,
            [rejectingItem.bookingId]: code,
          }))}
          onSubmit={(note) => handleRejectSubmit(rejectingItem, note)}
        />
      ) : null}
    </>
  );
}

export { resolveBandState, isRegisteredBand } from './bandState';
export type { BandKind, BandState } from './bandState';
